import type { TFunction } from 'i18next';
import { eAlertType } from '../providers/ToastProvider/Toast.enum';
import { tAlert } from '../providers/ToastProvider/Toast.types';
import { tErrorEntry } from './ErrorDirectory.types';

const entries: tErrorEntry[] = [
  // ─── TOAST ───
  {
    code: 1,
    titleKey: 'wapi.meta_1.title',
    messageKey: 'wapi.meta_1.message',
    displayMethod: 'toast',
  },
  // BUG-587: internal 3009 (WAPI_UNKNOWN_ERROR), whose Meta ancestor is 500 via
  // wba-10004. Previously this name pointed at 131000, which belongs to 3010.
  //
  // Keyed by WIRE CODE, not by the numeric 500, following the INT-1214 shape
  // below. lookupError resolves entries out of one namespace shared with the REST
  // envelope's `code`, so a numeric `500` entry would also answer a bare HTTP-500
  // envelope — and since chat_inline is not handled by useErrorHandler's switch,
  // that resolution is silent rather than merely wrong. The wire key keeps 3009
  // out of the numeric namespace entirely.
  //
  // toast (not chat_inline) so a wba-10004 REST envelope still alerts, as it did
  // before this ticket. The chat bubble is unaffected: 3009 has its own
  // renderMessageNotSupported case.
  {
    code: 'wba-10004',
    titleKey: 'wapi.meta_500.title',
    messageKey: 'wapi.meta_500.message',
    displayMethod: 'toast',
  },
  // BUG-587: Meta 40000 (wba-200000 → internal 3001, WAPI_CREDIT_CARD_EXPIRED_ERROR).
  // Account-level billing condition, so it keeps the banner treatment the name
  // previously inherited from its (incorrect) Meta 2 pointer.
  {
    code: 40000,
    titleKey: 'wapi.meta_40000.title',
    messageKey: 'wapi.meta_40000.message',
    displayMethod: 'banner',
    modules: ['all'],
  },
  {
    code: 4,
    titleKey: 'wapi.meta_4.title',
    messageKey: 'wapi.meta_4.message',
    displayMethod: 'toast',
  },
  {
    code: 33,
    titleKey: 'wapi.meta_33.title',
    messageKey: 'wapi.meta_33.message',
    displayMethod: 'toast',
  },
  {
    code: 100,
    titleKey: 'wapi.meta_100.title',
    messageKey: 'wapi.meta_100.message',
    displayMethod: 'toast',
  },
  {
    code: 131008,
    titleKey: 'wapi.meta_131008.title',
    messageKey: 'wapi.meta_131008.message',
    displayMethod: 'toast',
  },
  {
    code: 132012,
    titleKey: 'wapi.meta_132012.title',
    messageKey: 'wapi.meta_132012.message',
    displayMethod: 'toast',
  },
  {
    code: 132018,
    titleKey: 'wapi.meta_132018.title',
    messageKey: 'wapi.meta_132018.message',
    displayMethod: 'toast',
  },
  {
    code: 133000,
    titleKey: 'wapi.meta_133000.title',
    messageKey: 'wapi.meta_133000.message',
    displayMethod: 'toast',
  },
  {
    code: 133004,
    titleKey: 'wapi.meta_133004.title',
    messageKey: 'wapi.meta_133004.message',
    displayMethod: 'toast',
  },
  {
    code: 133005,
    titleKey: 'wapi.meta_133005.title',
    messageKey: 'wapi.meta_133005.message',
    displayMethod: 'toast',
  },
  {
    code: 133006,
    titleKey: 'wapi.meta_133006.title',
    messageKey: 'wapi.meta_133006.message',
    displayMethod: 'toast',
  },
  {
    code: 133015,
    titleKey: 'wapi.meta_133015.title',
    messageKey: 'wapi.meta_133015.message',
    displayMethod: 'toast',
  },
  // INT-1500 U7: Meta 133010 (number not registered on the API, the coex
  // activation family of INT-1516) and 131063 (marketing templates turned off
  // for the account). WBS mints wba-10123 and wba-10121; chats-service stores
  // 3073 and 3075. Toasts: the chat explains them in its own notice.
  {
    code: 133010,
    titleKey: 'wapi.meta_133010.title',
    messageKey: 'wapi.meta_133010.message',
    displayMethod: 'toast',
  },
  {
    code: 131063,
    titleKey: 'wapi.meta_131063.title',
    messageKey: 'wapi.meta_131063.message',
    displayMethod: 'toast',
  },
  // Permission range 200-299 sentinel
  {
    code: 'PERMISSION_RANGE',
    titleKey: 'wapi.meta_200.title',
    messageKey: 'wapi.meta_200.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10042',
    titleKey: 'wapi.wba_10042.title',
    messageKey: 'wapi.wba_10042.message',
    displayMethod: 'toast',
  },

  // ─── BANNER ───
  {
    code: 2,
    titleKey: 'wapi.meta_2.title',
    messageKey: 'wapi.meta_2.message',
    displayMethod: 'banner',
    modules: ['all'],
  },
  {
    code: 190,
    titleKey: 'wapi.meta_190.title',
    messageKey: 'wapi.meta_190.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 131005,
    titleKey: 'wapi.meta_131005.title',
    messageKey: 'wapi.meta_131005.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 130429,
    titleKey: 'wapi.meta_130429.title',
    messageKey: 'wapi.meta_130429.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 368,
    titleKey: 'wapi.meta_368.title',
    messageKey: 'wapi.meta_368.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 131057,
    titleKey: 'wapi.meta_131057.title',
    messageKey: 'wapi.meta_131057.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 131037,
    titleKey: 'wapi.meta_131037.title',
    messageKey: 'wapi.meta_131037.message',
    // chat_inline so the chat renders the dedicated title + message box from
    // ErrorDirectory (the approved BUG-582 copy) instead of the legacy
    // single-line reason. Aligns with the other chat_inline WAPI errors.
    displayMethod: 'chat_inline',
    modules: ['embudos', 'home', 'directorio', 'chats', 'wapi'],
  },
  {
    code: 131042,
    titleKey: 'wapi.meta_131042.title',
    messageKey: 'wapi.meta_131042.message',
    displayMethod: 'banner',
    modules: ['embudos', 'home', 'directorio', 'chats', 'wapi'],
  },
  {
    code: 131045,
    titleKey: 'wapi.meta_131045.title',
    messageKey: 'wapi.meta_131045.message',
    displayMethod: 'banner',
    modules: ['embudos', 'home', 'directorio', 'chats', 'wapi'],
  },
  {
    code: 131031,
    titleKey: 'wapi.meta_131031.title',
    messageKey: 'wapi.meta_131031.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },

  // ─── CHAT INLINE ───
  {
    code: 131048,
    titleKey: 'wapi.meta_131048.title',
    messageKey: 'wapi.meta_131048.message',
    displayMethod: 'chat_inline',
  },
  // INT-1225: Meta 131064 — the number hit its messaging limit (template
  // classification violations). Previously fell through to the wba-10026
  // catch-all. Arrives on a message via whatsapp_business.message.error like its
  // neighbours, hence chat_inline. No blockUntil: enforcement runs hours to DAYS,
  // so do not promise 24h. Not emitted by whatsapp-business-service yet: the
  // wba-10107 mapping lands in the INT-1225 PR B (MetaErrorMapper.go); this
  // entry is inert until then.
  {
    code: 131064,
    titleKey: 'wapi.meta_131064.title',
    messageKey: 'wapi.meta_131064.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131016,
    titleKey: 'wapi.meta_131016.title',
    messageKey: 'wapi.meta_131016.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131021,
    titleKey: 'wapi.meta_131021.title',
    messageKey: 'wapi.meta_131021.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131026,
    titleKey: 'wapi.meta_131026.title',
    messageKey: 'wapi.meta_131026.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131047,
    titleKey: 'wapi.meta_131047.title',
    messageKey: 'wapi.meta_131047.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 130472,
    titleKey: 'wapi.meta_130472.title',
    messageKey: 'wapi.meta_130472.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131049,
    titleKey: 'wapi.meta_131049.title',
    messageKey: 'wapi.meta_131049.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131050,
    titleKey: 'wapi.meta_131050.title',
    messageKey: 'wapi.meta_131050.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131051,
    titleKey: 'wapi.meta_131051.title',
    messageKey: 'wapi.meta_131051.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131052,
    titleKey: 'wapi.meta_131052.title',
    messageKey: 'wapi.meta_131052.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131053,
    titleKey: 'wapi.meta_131053.title',
    messageKey: 'wapi.meta_131053.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131000,
    titleKey: 'wapi.meta_131000.title',
    messageKey: 'wapi.meta_131000.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 132000,
    titleKey: 'wapi.meta_132000.title',
    messageKey: 'wapi.meta_132000.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 131060,
    titleKey: 'wapi.meta_131060.title',
    messageKey: 'wapi.meta_131060.message',
    displayMethod: 'chat_inline',
  },

  // ─── INPUT BLOCK ───
  // INT-1359: no blockUntil. 131056 is Meta's "(business, consumer) pair rate limit
  // hit", and Meta documents no wait window for it — its guidance is "wait and retry,
  // you can still send to a different phone number without waiting". The entry claimed
  // 'tomorrow', which is the same unsourced-window defect INT-1223 spent a release
  // undoing when it removed a 72h cooling-off period nobody could source. Do not
  // reinstate it from the 130497 sibling below: that one is 'permanent' because the
  // recipient is out of reach for good, which is a different fact. displayMethod stays
  // 'input_block' — 'toast' would newly render an addAlert on the template-send path.
  {
    code: 131056,
    titleKey: 'wapi.meta_131056.title',
    messageKey: 'wapi.meta_131056.message',
    displayMethod: 'input_block',
  },
  {
    code: 130497,
    titleKey: 'wapi.meta_130497.title',
    messageKey: 'wapi.meta_130497.message',
    displayMethod: 'input_block',
    blockUntil: 'permanent',
  },

  // ─── TEMPLATE BUTTON ───
  {
    code: 132001,
    titleKey: 'wapi.meta_132001.title',
    messageKey: 'wapi.meta_132001.message',
    displayMethod: 'template_button',
  },
  {
    code: 132005,
    titleKey: 'wapi.meta_132005.title',
    messageKey: 'wapi.meta_132005.message',
    displayMethod: 'template_button',
  },
  {
    code: 132007,
    titleKey: 'wapi.meta_132007.title',
    messageKey: 'wapi.meta_132007.message',
    displayMethod: 'template_button',
  },
  // INT-1225: toast, not template_button. useErrorHandler renders only toast and
  // banner; nothing in the app renders on template_button, so when WBS starts
  // answering 132015 with 422 + {code: "wba-10020"} (instead of a code-less 500
  // → 424) a failed 1:1 send would show nothing at all. The templates table and
  // the notification bell resolve these two entries by code and read
  // titleKey/messageKey directly, so they are unaffected. 132016 has the same
  // shape and would go silent the same way the day it gets a 4xx.
  {
    code: 132015,
    titleKey: 'wapi.meta_132015.title',
    messageKey: 'wapi.meta_132015.message',
    displayMethod: 'toast',
  },
  {
    code: 132016,
    titleKey: 'wapi.meta_132016.title',
    messageKey: 'wapi.meta_132016.message',
    displayMethod: 'toast',
  },
  // ─── BILLING / CREDIT ERRORS (wba-* codes from WriteMappedError) ───
  {
    code: 'wba-10024',
    titleKey: 'wapi.wba_10024.title',
    messageKey: 'wapi.wba_10024.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10025',
    titleKey: 'wapi.wba_10025.title',
    messageKey: 'wapi.wba_10025.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10055',
    titleKey: 'wapi.wba_10055.title',
    messageKey: 'wapi.wba_10055.message',
    displayMethod: 'toast',
  },
  // INT-766: campaign creation with the at-risk filter (exclude_at_risk)
  // rejected because every resolved recipient is at risk of spam blocking —
  // the audience would be empty, so no campaign was created. Source of truth:
  // whatsapp-business-service InternalErrorMapper.go (4023).
  {
    code: 'wba-10087',
    titleKey: 'wapi.wba_10087.title',
    messageKey: 'wapi.wba_10087.message',
    displayMethod: 'toast',
  },
  // BUG-1074: campaign creation, from Campañas or from Mensaje masivo, refused
  // because no Lead of the requested audience can receive a template (Leads of
  // other channels, or WhatsApp Handles a template cannot reach), so no
  // campaign was created. Both modals reach this entry through
  // useErrorHandler. Source of truth: whatsapp-business-service
  // InternalErrorMapper.go (4054).
  {
    code: 'wba-10124',
    titleKey: 'wapi.wba_10124.title',
    messageKey: 'wapi.wba_10124.message',
    displayMethod: 'toast',
  },
  // INT-1214: account-health circuit breaker (INT-1205, narrowed by INT-1223)
  // — marketing sends are paused while Meta has the number RESTRICTED. A spam
  // warning no longer reaches this code, and there is no cooling-off window:
  // the pause lifts when Meta reinstates the number. Utility templates and
  // session replies are never gated. Source of truth:
  // whatsapp-business-service InternalErrorMapper.go (4028).
  {
    code: 'wba-10092',
    titleKey: 'wapi.wba_10092.title',
    messageKey: 'wapi.wba_10092.message',
    displayMethod: 'toast',
  },
  // INT-1314: Meta's per-RECIPIENT marketing frequency cap (131049) is still in
  // force for this contact, so the template was deliberately not sent. Scoped to
  // one contact — the rest of the audience is unaffected — and it expires on its
  // own within ~24h. Deliberately separate from wba-10092, which pauses the
  // whole number: a customer who reads this as an account block would think the
  // problem INT-1223 fixed came back. Source of truth:
  // whatsapp-business-service InternalErrorMapper.go (4039).
  {
    code: 'wba-10104',
    titleKey: 'wapi.wba_10104.title',
    messageKey: 'wapi.wba_10104.message',
    displayMethod: 'toast',
  },
  // INT-1225: the recipient opted out of marketing (Meta 131050, "do not retry"),
  // so the template was deliberately not sent. Same pre-send rejection rail as
  // wba-10104, but its own entry: that copy promises the hold clears on its own
  // and this one never does (support reverses it manually). Not emitted by
  // whatsapp-business-service yet: the 4041 → wba-10108 mapping lands in the
  // INT-1225 PR B (InternalErrorMapper.go); this entry is inert until then.
  {
    code: 'wba-10108',
    titleKey: 'wapi.wba_10108.title',
    messageKey: 'wapi.wba_10108.message',
    displayMethod: 'toast',
  },
  // INT-1214: local per-number throughput limiter (INT-1205) exhausted its
  // wait budget — retryable, the send never reached Meta. Distinct from
  // Meta's own throttling codes (wba-10067 et al). Source of truth:
  // whatsapp-business-service InternalErrorMapper.go (4029).
  {
    code: 'wba-10093',
    titleKey: 'wapi.wba_10093.title',
    messageKey: 'wapi.wba_10093.message',
    displayMethod: 'toast',
  },
  // INT-1011: the attachment's bytes could not be uploaded to WhatsApp before
  // sending (strict media-upload mode) — the message was NOT sent, and a
  // plain resend re-uploads from scratch. Deliberately its own entry, not an
  // alias to Meta 131053: that copy blames the file's format/size, while this
  // failure is transient and the remedy is simply to retry. Arrives on the
  // message bubble via ChatEventError 3067. Source of truth:
  // whatsapp-business-service InternalErrorMapper.go (4040).
  {
    code: 'wba-10105',
    titleKey: 'wapi.wba_10105.title',
    messageKey: 'wapi.wba_10105.message',
    displayMethod: 'chat_inline',
  },
  // INT-914: embedded-signup connect — Meta's debug_token reported no
  // WhatsApp Business Account access for the exchanged token (HTTP 422 from
  // POST /whatsapp-api/account). The connect flow's authoritative surface is
  // the reducer-driven failure screen (ConnectionFailedError selects copy by
  // code via WHATSAPP_BUSINESS_NO_WABA_IN_TOKEN); this entry exists so any
  // other useErrorHandler consumer maps the code instead of falling through
  // to the generic toast. Source of truth: whatsapp-business-service
  // InternalErrorMapper.go (4009).
  {
    code: 'wba-10073',
    titleKey: 'wapi.wba_10073.title',
    messageKey: 'wapi.wba_10073.message',
    displayMethod: 'toast',
  },
  // Embedded-signup connect: the WhatsApp Business Account or phone
  // number selected in the Meta popup is already connected to another
  // Leadsales workspace, so the attempt was refused (HTTP 422 from POST
  // /whatsapp-api/account). The connect flow's authoritative surface is the
  // failure screen (ConnectionFailedError selects copy by code via
  // WHATSAPP_BUSINESS_WABA_IN_USE_BY_ANOTHER_WORKSPACE); this entry exists so
  // any other useErrorHandler consumer maps the code instead of falling
  // through to the generic toast. Deliberately absent from legacyToMetaMap:
  // this is our own cross-tenant guard, not a Meta error code, so there is
  // nothing to alias it to. Source of truth: whatsapp-business-service
  // InternalErrorMapper.go (4042).
  {
    code: 'wba-10109',
    titleKey: 'wapi.wba_10109.title',
    messageKey: 'wapi.wba_10109.message',
    displayMethod: 'toast',
  },

  // RET-2103 (delta 2): the SAME five wire codes as the `errorBusinessDirectory`
  // block above (wallet trio + the two pre-send guards) — registered a SECOND
  // time here on purpose, for a SECOND consumer.
  //
  // There are two independent template-send paths, each resolving errors
  // against its own table:
  //   - WITH variables  → WabTemplatesVariablesAction.hook.tsx → errorBusinessDirectory
  //   - WITHOUT variables → Template.component.tsx:322 → useErrorHandler → lookupError()
  //     over THIS `entries[]` array
  // Registering a code in one table does nothing for the other consumer: before
  // this delta, these five lived only in errorBusinessDirectory, so a
  // no-variables send that hit wba-10101/10102/10103 (wallet) or the two
  // MetaMessagingService guards fell through useErrorHandler's `!entry` arm and
  // painted the backend's raw `message` (English for the wallet trio).
  //
  // Do NOT "clean up" by deleting one of the two rows for a given code — that
  // would silently regress whichever send path you didn't test. If a wire code
  // is genuinely retired, remove it from BOTH tables in the same change.
  //
  // Zero copy duplication: messageKey points at the same flat key
  // errorBusinessDirectory already uses (delta 1) — wapi.walletInsufficientBalance,
  // wapi.walletUnavailable, wapi.walletUnpriceable, wapi.flowsUnboundButton,
  // wapi.templateHeaderMediaRequired. Only the title is new here: this table's
  // consumer (useErrorHandler) renders a title, errorBusinessDirectory's
  // consumer does not, so no titleKey existed to reuse.
  //
  // Known limit: this path does not branch by role (useErrorHandler has no
  // notion of role and none is added here) — wba-10101 always renders the
  // Owner/Admin copy. RET-1806's pre-send balance bar already tells a Seller
  // without top-up permission who to ask, before the send is even attempted.
  {
    code: 'wba-10101',
    titleKey: 'wapi.wba_10101.title',
    messageKey: 'wapi.walletInsufficientBalance',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10102',
    titleKey: 'wapi.wba_10102.title',
    messageKey: 'wapi.walletUnavailable',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10103',
    titleKey: 'wapi.wba_10103.title',
    messageKey: 'wapi.walletUnpriceable',
    displayMethod: 'toast',
  },
  {
    code: 'wba-flows-unbound-button',
    titleKey: 'wapi.wba_flows_unbound_button.title',
    messageKey: 'wapi.flowsUnboundButton',
    displayMethod: 'toast',
  },
  {
    code: 'wba-template-header-media-required',
    titleKey: 'wapi.wba_template_header_media_required.title',
    messageKey: 'wapi.templateHeaderMediaRequired',
    displayMethod: 'toast',
  },

  // ─── OUTBOUND SEND REFUSALS (plan pause) ───
  // While a workspace's plan is paused, every sender refuses the send with HTTP
  // 423 and a body of exactly {code, message}: messaging answers
  // WORKSPACE_PLAN_PAUSED / SENDER_WORKSPACE_MISMATCH /
  // SENDER_WORKSPACE_UNRESOLVABLE, and the WhatsApp API routes answer
  // wba-10113 / wba-10111 / wba-10112 (whatsapp-business-service
  // InternalErrorMapper.go 4043-4045), or WAPI_SEND_REFUSED when bff-web could
  // not read the upstream code. Registered here AND in errorBusinessDirectory,
  // for the same two-consumer reason as the wba-10101 rows above.
  //
  // The pause is wba-10113, never wba-10110. whatsapp-business-service already
  // answers wba-10110 in production for Meta 2494100 (BUG-908, MetaErrorMapper),
  // a failure that has nothing to do with a pause, so the pause moved to 10113
  // (whatsapp-business-service #1123) and wba-10110 stays unregistered here on
  // purpose: a Meta failure must never read "your plan is paused", nor start
  // the subscription reconcile that notifyPlanPauseRefusal triggers.
  //
  // Only the paused codes may say "paused". The mismatch and unresolvable
  // codes mean the send could not be tied to its workspace, which can refuse
  // a workspace that is not paused at all, so they share neutral copy with
  // any refusal we cannot read. No copy carries a date: screens that show a
  // pause's dates read them from the billing summary.
  //
  // Keyed by code, not number, like the INT-1214 shape: lookupError parses the
  // key with parseInt, and none of these is numeric.
  {
    code: 'WORKSPACE_PLAN_PAUSED',
    titleKey: 'outboundRefusal.paused.title',
    messageKey: 'outboundRefusal.paused.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10113',
    titleKey: 'outboundRefusal.paused.title',
    messageKey: 'outboundRefusal.paused.message',
    displayMethod: 'toast',
  },
  {
    code: 'SENDER_WORKSPACE_MISMATCH',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    displayMethod: 'toast',
  },
  {
    code: 'SENDER_WORKSPACE_UNRESOLVABLE',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10111',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    displayMethod: 'toast',
  },
  {
    code: 'wba-10112',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    displayMethod: 'toast',
  },
  {
    code: 'WAPI_SEND_REFUSED',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    displayMethod: 'toast',
  },

  // ─── BUG-548: codes promoted out of the wba-10026 fallback ───
  // Each previously surfaced as the generic "Meta no puede visualizar este
  // mensaje" tooltip. The displayMethod is picked to mirror the existing
  // entry for the same UX category (banner for auth/account-wide blockers,
  // chat_inline for per-recipient failures, etc.).
  {
    code: 0,
    titleKey: 'wapi.meta_0.title',
    messageKey: 'wapi.meta_0.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 3,
    titleKey: 'wapi.meta_3.title',
    messageKey: 'wapi.meta_3.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 10,
    titleKey: 'wapi.meta_10.title',
    messageKey: 'wapi.meta_10.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 80007,
    titleKey: 'wapi.meta_80007.title',
    messageKey: 'wapi.meta_80007.message',
    displayMethod: 'banner',
    modules: ['embudos', 'chats', 'wapi', 'home'],
  },
  {
    code: 130403,
    titleKey: 'wapi.meta_130403.title',
    messageKey: 'wapi.meta_130403.message',
    displayMethod: 'chat_inline',
  },
  // 130429 already exists above as `code: 130429` (banner). The legacyToMetaMap
  // below now routes `wba-10067` to it.
  // 130472 already exists above as `code: 130472` (chat_inline). The
  // legacyToMetaMap below now routes `wba-10068` to it.
  // 130497 already exists above as `code: 130497` (input_block, permanent).
  // The legacyToMetaMap below now routes `wba-10069` to it.
  {
    code: 131030,
    titleKey: 'wapi.meta_131030.title',
    messageKey: 'wapi.meta_131030.message',
    displayMethod: 'chat_inline',
  },
  {
    code: 2388293,
    titleKey: 'wapi.meta_2388293.title',
    messageKey: 'wapi.meta_2388293.message',
    displayMethod: 'template_button',
  },
  {
    code: 2388299,
    titleKey: 'wapi.meta_2388299.title',
    messageKey: 'wapi.meta_2388299.message',
    displayMethod: 'template_button',
  },
];

// ─── PLAN PAUSE REFUSALS (billing) ───
// leadbilling refuses a plan-pause operation (cancel a scheduled pause, preview
// or pay an early resume), and any subscription change while a pause is
// scheduled or live, with a machine code. bff-web relays it as a body of
// exactly {code, message} with the upstream status (BillingRefusalError,
// billing.refusals.go); a pause that moved under the request answers 409. The
// `message` is bff-web's English fallback and never UI text.
//
// One home per sentence, like the outbound refusals above: every
// useErrorHandler consumer (these entries) and the pause card
// (lookupPlanPauseRefusalByCode) read the same errors/errors keys.
//
// PLAN_PAUSE_CANCEL_WINDOW_CLOSED is what leadbilling answers today for a
// scheduled pause too close to its start (CancelScheduledPlanPauseUseCase);
// bff-web's copy list knows the same refusal as PLAN_PAUSE_TOO_LATE_TO_CANCEL.
// Both read the same sentence.
const PLAN_PAUSE_REFUSAL_KEYS = new Map<string, string>([
  ['PLAN_PAUSE_ACTIVE', 'planPauseRefusal.active'],
  ['PLAN_PAUSE_SCHEDULE_FAILED', 'planPauseRefusal.scheduleFailed'],
  ['PLAN_PAUSE_NOT_FOUND', 'planPauseRefusal.notFound'],
  ['PLAN_PAUSE_NOT_ACTIVE', 'planPauseRefusal.notActive'],
  ['PLAN_PAUSE_NOT_SCHEDULED', 'planPauseRefusal.notScheduled'],
  ['PLAN_PAUSE_TOO_LATE_TO_CANCEL', 'planPauseRefusal.tooLateToCancel'],
  ['PLAN_PAUSE_CANCEL_WINDOW_CLOSED', 'planPauseRefusal.tooLateToCancel'],
  ['PLAN_PAUSE_PREVIEW_EXPIRED', 'planPauseRefusal.previewExpired'],
  [
    'PLAN_PAUSE_RESUME_AMOUNT_UNAVAILABLE',
    'planPauseRefusal.resumeAmountUnavailable',
  ],
  ['PLAN_PAUSE_SUBSCRIPTION_CHANGED', 'planPauseRefusal.subscriptionChanged'],
  ['PLAN_PAUSE_SUBSCRIPTION_PAST_DUE', 'planPauseRefusal.subscriptionPastDue'],
  [
    'PLAN_PAUSE_SUBSCRIPTION_NOT_ACTIVE',
    'planPauseRefusal.subscriptionNotActive',
  ],
  ['PLAN_PAUSE_OPEN_INVOICE', 'planPauseRefusal.openInvoice'],
  [
    'PLAN_PAUSE_PAYMENT_METHOD_REQUIRED',
    'planPauseRefusal.paymentMethodRequired',
  ],
  ['PLAN_PAUSE_OPERATION_IN_PROGRESS', 'planPauseRefusal.operationInProgress'],
  ['PLAN_PAUSE_STATE_CHANGED', 'planPauseRefusal.stateChanged'],
]);

const planPauseRefusalEntries: tErrorEntry[] = Array.from(
  PLAN_PAUSE_REFUSAL_KEYS,
  ([code, key]) => ({
    code,
    titleKey: `${key}.title`,
    messageKey: `${key}.message`,
    displayMethod: 'toast',
  }),
);

export const errorDirectory = new Map<string, tErrorEntry>(
  [...entries, ...planPauseRefusalEntries].map((entry) => [
    String(entry.code),
    entry,
  ]),
);

export function lookupError(code: number | string): tErrorEntry | undefined {
  const direct = errorDirectory.get(String(code));
  if (direct) return direct;

  const numCode = typeof code === 'string' ? parseInt(code, 10) : code;
  if (!isNaN(numCode) && numCode >= 200 && numCode <= 299) {
    return errorDirectory.get('PERMISSION_RANGE');
  }

  return undefined;
}

/**
 * Meta's WhatsApp Cloud API error-codes table.
 *
 * Deliberately the bare page, with no `#:~:text=` fragment. A text fragment
 * does not survive this navigation — Meta renders that table on the client, so
 * the text the browser is asked to match does not exist when it looks.
 */
export const META_ERROR_DOCS =
  'https://developers.facebook.com/documentation/business-messaging/whatsapp/support/error-codes';

/**
 * Whether a code has a row in the table above.
 *
 * Numeric codes are Meta's and are documented. Wire codes (`wba-*`) are ours —
 * `wba-10105`, for instance, comes from whatsapp-business-service's
 * InternalErrorMapper and Meta has never heard of it — so linking one would
 * send a seller to a page that cannot explain their error. Showing the code is
 * still worth it either way; only the link is conditional.
 */
export function isMetaDocumentedCode(code: number | string): boolean {
  return typeof code === 'number';
}

export const legacyToMetaMap: Record<string, number | string> = {
  'wba-10001': 2,
  'wba-10002': 100,
  'wba-10003': 190,
  // BUG-587: was 1. wba-10004 is Meta 500 (MetaErrorMapper.go); Meta 1 is
  // wba-10000. This table carried the same one-row shift as the name map, so
  // the REST path showed the Meta 1 'try again in 1 hour' toast for a
  // condition the chat bubble now calls a one-minute transient.
  'wba-10000': 1,
  'wba-200000': 40000,
  'wba-10005': 131000,
  'wba-10006': 131008,
  'wba-10007': 131026,
  'wba-10008': 131031,
  'wba-10009': 131037,
  'wba-10010': 131042,
  'wba-10011': 131047,
  'wba-10012': 131048,
  'wba-10014': 131052,
  'wba-10015': 131053,
  'wba-10016': 131056,
  'wba-10017': 131057,
  'wba-10018': 132000,
  'wba-10019': 132001,
  'wba-10020': 132015,
  'wba-10021': 132016,
  'wba-10027': 33,
  'wba-10028': 'PERMISSION_RANGE',
  'wba-10029': 368,
  'wba-10030': 131005,
  'wba-10031': 131016,
  'wba-10032': 131021,
  'wba-10033': 131045,
  'wba-10034': 131049,
  'wba-10035': 131050,
  'wba-10036': 132005,
  'wba-10037': 132007,
  'wba-10038': 132012,
  'wba-10039': 132018,
  'wba-10040': 133000,
  'wba-10041': 133004,
  'wba-10050': 130429,
  'wba-10051': 130497,
  'wba-10052': 130472,
  'wba-10053': 131051,
  'wba-10056': 4,
  'wba-10057': 133005,
  'wba-10058': 133006,
  'wba-10059': 133015,
  // BUG-548: 11 wba codes promoted out of the wba-10026 fallback.
  // We KEEP the legacy `wba-10052: 130472` entry above untouched to avoid
  // breaking older consumers that learned that alias; new code should prefer
  // `wba-10068` as the canonical alias for Meta 130472.
  'wba-10062': 0,
  'wba-10063': 3,
  'wba-10064': 10,
  'wba-10065': 80007,
  'wba-10066': 130403,
  'wba-10067': 130429,
  'wba-10068': 130472,
  'wba-10069': 130497,
  'wba-10070': 131030,
  'wba-10071': 2388293,
  'wba-10072': 2388299,
  // INT-1225: Meta 131064 messaging limit. Minted in the WBS PR B (MetaErrorMapper.go).
  'wba-10107': 131064,
  // INT-1500 U7: minted by WBS MetaErrorMapper.go for Meta 133010 and 131063.
  'wba-10123': 133010,
  'wba-10121': 131063,
};

// ─── MessageErrorType → Meta code mapping ───
// Maps the string values from MessageErrorType enum to their corresponding
// Meta error codes in the ErrorDirectory.
export const messageErrorTypeToMetaCode: Record<string, number | string> = {
  // ─── TOAST ───
  // BUG-587: Meta 1's real owner is internal 3002 (wba-10000). Repointing
  // WAPI_GENERAL_FAILURE_ERROR to 131000 left Meta 1's entry orphaned.
  WAPI_API_UNKNOWN_ERROR: 1,
  WAPI_INVALID_PARAM_VALUE_ERROR: 33,
  WAPI_INVALID_PARAMETER_ERROR: 100,
  WAPI_MISSING_PARAMETER_ERROR: 131008,
  WAPI_PARAMETER_FORMAT_MISMATCH_ERROR: 132012,
  WAPI_TEMPLATE_INVALID_PARAMS_ERROR: 132018,
  WAPI_INCOMPLETE_DEREGISTRATION_ERROR: 133000,
  WAPI_REGISTRATION_SERVER_UNAVAILABLE_ERROR: 133004,
  WAPI_PERMISSION_ERROR: 'PERMISSION_RANGE',

  // ─── BANNER ───
  // BUG-587: was 2. Meta 2 is "API Service unavailable" (wba-10001 → internal
  // 3003); the credit-card condition is Meta 40000 (wba-200000 → internal 3001).
  WAPI_CREDIT_CARD_EXPIRED_ERROR: 40000,
  WAPI_ACCESS_TOKEN_ERROR: 190,
  WAPI_ACCESS_DENIED_ERROR: 131005,
  WAPI_TEMPORARILY_BLOCKED_ERROR: 368,
  WAPI_DISCONNECTED_ACCOUNT_ERROR: 131031,
  WAPI_DISPLAY_NAME_PENDING_ERROR: 131037,
  WAPI_BUSINESS_ISSUE_ERROR: 131042,
  WAPI_CERTIFICATE_ERROR: 131045,
  WAPI_CONCURRENCY_LIMIT_ERROR: 131057,

  // ─── CHAT INLINE ───
  // BUG-587: these three were shifted one wba- row apart from the backend chain.
  // 3010 (wba-10005) is Meta 131000; 3009 (wba-10004) is Meta 500. Repointing
  // 3010 at 131000 also moves it from toast to chat_inline, so the bubble body
  // renders the canonical card. The tooltip still reads chat.json — see U5.
  WAPI_GENERAL_FAILURE_ERROR: 131000,
  WAPI_UNKNOWN_ERROR: 'wba-10004',
  // 3003 has TWO Meta ancestors: 2 (wba-10001) and 131016 (wba-10031). The chain
  // is not invertible here, so this is a stated routing decision, not a derived
  // fact: 131016 is already chat_inline, so the body renders canonical copy.
  WAPI_SERVICE_UNAVAILABLE_ERROR: 131016,
  WAPI_SERVICE_UNAVAILABLE_ERROR_2: 131016,
  WAPI_RECIPIENT_SENDER_CONFLICT_ERROR: 131021,
  WAPI_MESSAGE_UNDELIVERABLE_ERROR: 131026,
  WAPI_REENGAGEMENT_REQUIRED_ERROR: 131047,
  WAPI_RATE_LIMITED_ERROR: 131048,
  // INT-1214: local throughput-limiter rejection maps to its own wba entry,
  // NOT to 131048 — that copy blames "marked as spam", this one is retryable.
  WAPI_SEND_RATE_LIMITED_ERROR: 'wba-10093',
  WAPI_HEALTHY_ENGAGEMENT_ERROR: 131049,
  WAPI_USER_STOPPED_MARKETING_ERROR: 131050,
  // BUG-587: WAPI_UNSUPPORTED_MESSAGE_ERROR was removed here. parseErrors never
  // returns that name — the backend emits WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR
  // (internal 3050) — so the entry was dead and matched nothing.
  //
  // It is deliberately DELETED rather than repointed at the produced name:
  // 131051's entry is chat_inline, so repointing would move 3050 off its error
  // rail onto the inline card and reverse BUG-704, which chose the rail on
  // purpose ("3050 keeps its rail with rewritten honest copy", Chat.enum.ts).
  // 3050 already renders correct dedicated copy through its own
  // renderMessageNotSupported case, so nothing is lost.
  WAPI_POSSIBLE_UNSUPPORTED_DEVICE_ERROR: 131060,
  WAPI_MEDIA_DOWNLOAD_ERROR: 131052,
  WAPI_MEDIA_UPLOAD_ERROR: 131053,
  // INT-1011: our own upload hop failed (strict mode) — maps to its own wba
  // entry, NOT to 131053, whose copy blames the file's format/size while this
  // failure is retryable and the message was never sent.
  WAPI_OUTBOUND_MEDIA_UPLOAD_ERROR: 'wba-10105',
  WAPI_TEMPLATE_PARAM_COUNT_ERROR: 132000,

  // ─── INPUT BLOCK ───
  WAPI_PAIR_RATE_LIMIT_ERROR: 131056,

  // ─── TEMPLATE BUTTON ───
  WAPI_TEMPLATE_NOT_APPROVED_ERROR: 132001,
  WAPI_HYDRATED_TEXT_TOO_LONG_ERROR: 132005,
  WAPI_FORMAT_POLICY_VIOLATION_ERROR: 132007,
  WAPI_TEMPLATE_PAUSED_ERROR: 132015,
  WAPI_TEMPLATE_DISABLED_ERROR: 132016,

  // ─── BUG-548: 11 codes promoted out of the wba-10026 fallback ───
  WAPI_AUTH_EXCEPTION_ERROR: 0,
  WAPI_API_METHOD_ERROR: 3,
  WAPI_PERMISSION_DENIED_ERROR: 10,
  WAPI_ACCOUNT_RATE_LIMIT_ERROR: 80007,
  WAPI_BUSINESS_BLOCKED_USER_ERROR: 130403,
  WAPI_RATE_LIMIT_HIT_ERROR: 130429,
  WAPI_RECIPIENT_EXPERIMENT_ERROR: 130472,
  WAPI_MESSAGING_RESTRICTED_COUNTRY_ERROR: 130497,
  WAPI_RECIPIENT_NOT_ALLOWED_ERROR: 131030,
  WAPI_TEMPLATE_WORD_RATIO_ERROR: 2388293,
  WAPI_TEMPLATE_LEADING_TRAILING_PARAM_ERROR: 2388299,

  // ─── INT-1500 U7: Meta codes chats-service gained (contract C2) ───
  WAPI_NUMBER_NOT_REGISTERED_ERROR: 133010,
  WAPI_MARKETING_DISABLED_ERROR: 131063,
};

export function lookupByMessageErrorType(
  errorType: string,
): tErrorEntry | undefined {
  const metaCode = messageErrorTypeToMetaCode[errorType];
  if (metaCode === undefined) return undefined;
  return lookupError(metaCode);
}

// Legacy export for backward compatibility during migration.
// Consumers still use .find(e => e.code === errorMeta.error) with wba-XXXXX codes.
// This will be removed once all consumers migrate to useErrorHandler (Phase 4).
export const errorBusinessDirectory = [
  { code: 'wba-10001', message: 'wapi.temporaryErrorOrNetwork' },
  { code: 'wba-10002', message: 'wapi.invalidParameter' },
  { code: 'wba-10003', message: 'wapi.tokenIssue' },
  { code: 'wba-10004', message: 'wapi.internalMetaError' },
  { code: 'wba-10005', message: 'wapi.genericError' },
  { code: 'wba-10006', message: 'wapi.missingParameter' },
  { code: 'wba-10007', message: 'wapi.messageUndeliverable' },
  { code: 'wba-10008', message: 'wapi.accountSuspended' },
  { code: 'wba-10009', message: 'wapi.displayNamePending' },
  { code: 'wba-10010', message: 'wapi.paymentOrEligibility' },
  { code: 'wba-10011', message: 'wapi.reengagementOver24h' },
  { code: 'wba-10012', message: 'wapi.rateLimitSpam' },
  { code: 'wba-10013', message: 'wapi.unsupportedMessageTypeError' },
  { code: 'wba-10014', message: 'wapi.mediaDownloadFailure' },
  { code: 'wba-10015', message: 'wapi.mediaUploadFailure' },
  { code: 'wba-10016', message: 'wapi.tooManyMessagesSameUser' },
  { code: 'wba-10017', message: 'wapi.limitConcurrentSends' },
  { code: 'wba-10018', message: 'wapi.variableCountMismatch' },
  { code: 'wba-10019', message: 'wapi.templateNotExistOrNotApproved' },
  { code: 'wba-10020', message: 'wapi.templatePaused' },
  { code: 'wba-10021', message: 'wapi.templateDisabled' },
  { code: 'wba-10022', message: 'wapi.mediaDownloadError' },
  { code: 'wba-10023', message: 'wapi.businessEligibility' },
  { code: 'wba-10024', message: 'wapi.insufficientCreditsMassMessages' },
  { code: 'wba-10025', message: 'wapi.insufficientCreditsChatOneToOne' },
  { code: 'wba-10026', message: 'wapi.genericMessage' },
  { code: 'wba-10027', message: 'wapi.parameterValueIsNotValid' },
  { code: 'wba-10028', message: 'wapi.permissionError' },
  { code: 'wba-10029', message: 'wapi.temporarilyBlocked' },
  { code: 'wba-10030', message: 'wapi.accessDenied' },
  { code: 'wba-10031', message: 'wapi.serviceUnavailable' },
  { code: 'wba-10032', message: 'wapi.recipientCannotBeSender' },
  { code: 'wba-10033', message: 'wapi.incorrectCertificateRegistration' },
  { code: 'wba-10034', message: 'wapi.healthyEngagementProtection' },
  { code: 'wba-10035', message: 'wapi.userStoppedMarketing' },
  { code: 'wba-10036', message: 'wapi.hydratedTextTooLong' },
  { code: 'wba-10037', message: 'wapi.characterFormatPolicyViolated' },
  { code: 'wba-10038', message: 'wapi.parameterFormatMismatch' },
  { code: 'wba-10039', message: 'wapi.templateInvalidParamsDiagnostic' },
  { code: 'wba-10040', message: 'wapi.incompleteDeregistration' },
  { code: 'wba-10041', message: 'wapi.registrationServerUnavailable' },
  { code: 'wba-10042', message: 'wapi.duplicateLanguageContent' },
  { code: 'wba-10043', message: 'wapi.messageTemplateLimitExceeded' },
  { code: 'wba-10044', message: 'wapi.characterLimitExceeded' },
  { code: 'wba-10045', message: 'wapi.componentsMissingExpectedFields' },
  { code: 'wba-10046', message: 'wapi.messageHeaderFormatIncorrect' },
  { code: 'wba-10047', message: 'wapi.messageBodyFormatIncorrect' },
  { code: 'wba-10048', message: 'wapi.messageFooterFormatIncorrect' },
  { code: 'wba-10049', message: 'wapi.templateRecentlyDeleted' },
  { code: 'wba-10050', message: 'wapi.rateLimitHit' },
  { code: 'wba-10051', message: 'wapi.messagingRestrictedCountry' },
  { code: 'wba-10052', message: 'wapi.recipientExperiment' },
  { code: 'wba-10053', message: 'wapi.unsupportedMessage' },
  { code: 'wba-10055', message: 'wapi.serviceUnavailable' },
  { code: 'wba-10056', message: 'wapi.tooManyApiCalls' },
  { code: 'wba-10057', message: 'wapi.twoStepVerificationPinMismatch' },
  { code: 'wba-10058', message: 'wapi.phoneNumberRequiresReverification' },
  { code: 'wba-10059', message: 'wapi.recentlyDeregistered' },
  // RET-2103: whatsapp-business-service's wallet send gate
  // (InternalErrorMapper.go 4036/4037/4038) blocks a template send with one
  // of these three wire codes and none was registered anywhere in the front,
  // so the send path fell through to concatenating the backend's raw English
  // `message`. Distinct copy per code on purpose: only wba-10101 (insufficient
  // balance) should send the customer to top up — wba-10102 (wallet didn't
  // answer) is retryable, and wba-10103 (couldn't be priced) is neither an
  // out-of-money case nor fixed by recharging. Kept separate from
  // wba-10024/wba-10025 above: those are plan credits and route to upgrade,
  // these are wallet balance and route to top-up — different products.
  { code: 'wba-10101', message: 'wapi.walletInsufficientBalance' },
  { code: 'wba-10102', message: 'wapi.walletUnavailable' },
  { code: 'wba-10103', message: 'wapi.walletUnpriceable' },
  // RET-2103 (round 2): whatsapp-business-service has TWO pre-send guards in
  // the shared trunk (`executeTemplateSend`, MetaMessagingService.go:548 and
  // :571) that already return their own wire code AND a Spanish, actionable
  // phrase — the customer reads that phrase today only because the old
  // send path fell through to the backend's raw `message`. "Never paint the
  // message" would silently turn both into the generic unknown-error copy
  // (a regression of INT-1098 and BUG-866), so the front registers its OWN
  // copy for these two codes instead, exactly like it does for the wallet.
  { code: 'wba-flows-unbound-button', message: 'wapi.flowsUnboundButton' },
  {
    code: 'wba-template-header-media-required',
    message: 'wapi.templateHeaderMediaRequired',
  },
  // Outbound send refusals: the same seven codes as the entries[] block, with
  // the same message keys. See the note there.
  { code: 'WORKSPACE_PLAN_PAUSED', message: 'outboundRefusal.paused.message' },
  { code: 'wba-10113', message: 'outboundRefusal.paused.message' },
  {
    code: 'SENDER_WORKSPACE_MISMATCH',
    message: 'outboundRefusal.refused.message',
  },
  {
    code: 'SENDER_WORKSPACE_UNRESOLVABLE',
    message: 'outboundRefusal.refused.message',
  },
  { code: 'wba-10111', message: 'outboundRefusal.refused.message' },
  { code: 'wba-10112', message: 'outboundRefusal.refused.message' },
  { code: 'WAPI_SEND_REFUSED', message: 'outboundRefusal.refused.message' },
];

// ─── Outbound send refusals ───

export type tOutboundRefusalKind = 'paused' | 'refused';

/**
 * How a refused send reads to the customer. `bubbleKey` is the short line that
 * sits under a message that was not sent; `titleKey` and `messageKey` are the
 * toast (the same keys as the directory entries above).
 */
export type tOutboundRefusal = {
  kind: tOutboundRefusalKind;
  titleKey: string;
  messageKey: string;
  bubbleKey: string;
};

const OUTBOUND_REFUSALS: Record<tOutboundRefusalKind, tOutboundRefusal> = {
  paused: {
    kind: 'paused',
    titleKey: 'outboundRefusal.paused.title',
    messageKey: 'outboundRefusal.paused.message',
    bubbleKey: 'outboundRefusal.paused.bubble',
  },
  refused: {
    kind: 'refused',
    titleKey: 'outboundRefusal.refused.title',
    messageKey: 'outboundRefusal.refused.message',
    bubbleKey: 'outboundRefusal.refused.bubble',
  },
};

/**
 * Every code a sender refuses a send with, by what the customer is told. The
 * numbers are eChatEventErrorCodes 1016-1018 (Sockets.enums.ts), written as
 * literals so this module does not load the socket enums: tests that mock
 * that module partially would otherwise fail on import.
 */
const OUTBOUND_REFUSAL_KIND_BY_CODE: Record<string, tOutboundRefusalKind> = {
  WORKSPACE_PLAN_PAUSED: 'paused',
  'wba-10113': 'paused',
  1016: 'paused',
  SENDER_WORKSPACE_MISMATCH: 'refused',
  SENDER_WORKSPACE_UNRESOLVABLE: 'refused',
  'wba-10111': 'refused',
  'wba-10112': 'refused',
  WAPI_SEND_REFUSED: 'refused',
  1017: 'refused',
  1018: 'refused',
};

/** The status every sender answers a refused send with. */
export const OUTBOUND_REFUSAL_HTTP_STATUS = 423;

/** Tells the subscription provider to reconcile after a confirmed pause refusal. */
export function notifyPlanPauseRefusal(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('leadsales:plan-pause-refusal'));
}

/**
 * The refusal behind a code: an HTTP refusal code, a whatsapp-business-service
 * wire code, a chat error number (1016-1018) or the MessageErrorType it parses
 * to. Undefined for any other code.
 */
export function lookupOutboundRefusalByCode(
  code: string | number | undefined,
): tOutboundRefusal | undefined {
  if (code === undefined || code === '') return undefined;
  const kind = OUTBOUND_REFUSAL_KIND_BY_CODE[String(code)];
  return kind ? OUTBOUND_REFUSALS[kind] : undefined;
}

/**
 * The refusal among a chat event's stored errors, if one of them is a refusal
 * (chat codes 1016-1018). A follow-up that came due during a pause is stored
 * this way, with the server's English refusal as its context.
 */
export function findOutboundRefusal(
  errors: ReadonlyArray<{ code?: number | string }> | undefined,
): tOutboundRefusal | undefined {
  for (const entry of errors ?? []) {
    const refusal = lookupOutboundRefusalByCode(entry.code);
    if (refusal) return refusal;
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * The refusal behind a rejected request, or undefined when the request failed
 * for any other reason.
 *
 * Reads the top-level `code` of the response body, or `error`, where
 * whatsapp-business-service mirrors it. A 423 whose code is missing or unknown
 * is still a refused send, so it gets the neutral refusal: it must never read
 * as a pause, and never as axios's "Request failed with status code 423".
 */
export function lookupOutboundRefusal(
  error: unknown,
): tOutboundRefusal | undefined {
  if (!isRecord(error) || !isRecord(error.response)) return undefined;

  const { status, data } = error.response;
  const body = isRecord(data) ? data : undefined;
  const rawCode = body?.code ?? body?.error;
  const code = typeof rawCode === 'string' ? rawCode.trim() : undefined;

  const refusal = lookupOutboundRefusalByCode(code);
  if (refusal) return refusal;

  return status === OUTBOUND_REFUSAL_HTTP_STATUS
    ? OUTBOUND_REFUSALS.refused
    : undefined;
}

/**
 * The toast for a refused send, the same on every surface. A paused plan is an
 * expected state, not a malfunction, so it warns; a refusal we cannot explain
 * is an error.
 *
 * `t` may belong to any namespace: the copy is read from `errors/errors`, which
 * the calling hook must also declare in its useTranslation.
 */
export function buildOutboundRefusalAlert(
  refusal: tOutboundRefusal,
  t: TFunction,
): tAlert {
  if (refusal.kind === 'paused') notifyPlanPauseRefusal();
  return {
    type: refusal.kind === 'paused' ? eAlertType.WARNING : eAlertType.ERROR,
    title: t(refusal.titleKey, { ns: 'errors/errors' }),
    message: t(refusal.messageKey, { ns: 'errors/errors' }),
  };
}

// ─── Plan pause refusals ───

/** How a billing plan-pause refusal reads; the keys live in `errors/errors`. */
export type tPlanPauseRefusal = {
  code: string;
  titleKey: string;
  messageKey: string;
};

/**
 * The copy for a billing plan-pause refusal code (see PLAN_PAUSE_REFUSAL_KEYS),
 * or undefined for any other value: an unknown code, a status code, no code.
 */
export function lookupPlanPauseRefusalByCode(
  code: unknown,
): tPlanPauseRefusal | undefined {
  if (typeof code !== 'string') return undefined;
  const trimmed = code.trim();
  const key = PLAN_PAUSE_REFUSAL_KEYS.get(trimmed);
  return key
    ? { code: trimmed, titleKey: `${key}.title`, messageKey: `${key}.message` }
    : undefined;
}
